import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../stores/auth';
import { useGroupStore } from '../stores/group';
import { useInvitationStore } from '../stores/invitation';
import { useWebSocketStore } from '../stores/websocket';
import { getUserIdentifierFromUser } from '../lib/auth';
import type { PermissionKey } from '@safetyhub/shared';
import {
  isNavItemVisible,
  resolveNavItemPath,
  resolveNavVisibilityContext,
} from './layoutNavVisibility';
import OrganizationSelector from './OrganizationSelector';
import GroupSelector from './GroupSelector';
import UsageIndicator from './UsageIndicator';
import InvitationModal from './modals/InvitationModal';
import ImpersonationBanner from './ImpersonationBanner';
import FeedbackModal from './feedback/FeedbackModal';
import { captureScreenshot } from '../utils/diagnostics';
import VerificationPendingBanner from './VerificationPendingBanner';
import PopupHost from './PopupHost';
import ActionItemsBar from './ActionItemsBar';
import AnnouncementBand from './AnnouncementBand';
import MyDuplicateModal from './MyDuplicateModal';
import NotificationDropdown from './NotificationDropdown';
import { ServerEvents, UserEventPayload, ReviewRequestedPayload } from '../types/websocket';
import { useToastStore } from '../stores/toast';
import { useNotificationStore } from '../stores/notification';
import { useDeviceLayout } from '../hooks/useDeviceLayout';
import { useFabPermissions } from '../hooks/useFabPermissions';
import { useFabSuppression } from '../hooks/useFabSuppression';
import { useLegalDocumentsVisibility } from '../hooks/useLegalDocumentsVisibility';
import { CalendarIcon, CheckSquareIcon } from './icons';
import { useModalStore } from '../stores/modal';
import { SpeedDialFab, type SpeedDialItem } from './fab/SpeedDialFab';
import { FactoryIcon, ClipboardCheckIcon as FabClipboardCheckIcon, FileTextIcon } from './fab/FabIcons';
interface LayoutProps {
  children: React.ReactNode;
}

interface MenuItem {
  name: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  ownerOnly?: boolean;
  memberOnly?: boolean;
  businessOnly?: boolean;
  /** 권한 key 축(합집합) — 그 항목이 가리키는 페이지의 진입 게이트와 같은 키. `layoutNavVisibility.ts` doc */
  anyPermission?: readonly PermissionKey[];
  /** 자식을 품은 항목에서 **자기 페이지**가 요구하는 키 — 없으면 링크가 첫 가시 자식으로 간다 */
  selfPermission?: PermissionKey;
  /** 피처 플래그 뒤 항목 — 가시성은 `navFlagGates`가 키마다 주입한다(`layoutNavVisibility.ts` doc) */
  flagKey?: string;
  /** 데스크톱 사이드바의 시각 그룹핑용 메타 (하위 항목은 부모를 따르므로 없음) */
  group?: string;
  /** 사이드바 하위 메뉴. 펼침은 라우트에서 파생되며 별도 토글 상태를 두지 않는다. */
  children?: MenuItem[];
}

// 메인 메뉴 — group은 데스크톱 사이드바의 시각 그룹핑용 메타(항목·라우트·권한 필터와 무관)
const menuItems: MenuItem[] = [
  { name: '대시보드', path: '/', icon: HomeIcon, group: 'main' },
  { name: '스케줄 관리', path: '/schedules', icon: CalendarIcon, group: 'main' },
  // 점검 → 문서 → 결재 순서는 실제 업무 흐름이다(점검을 수행해야 보고서가 나오고 그걸 결재한다).
  // `/inspections`는 현장 파라미터 없이 동작하는 전역 목록이라, 종전처럼 현장 관리를 거칠 필요가 없다.
  { name: '점검 관리', path: '/inspections', icon: CheckSquareIcon, group: 'work' },
  // 요구 → 산출 → 승인 순서(결정 `2026-10-23-legal-document-obligation-management.md` ⓓ). `/reports`의
  // 탭이 아니라 형제다 — 그 탭 축은 상태·`mine`이고 의무 축과 다르다. 가시성은 서버 visibility(아래 `navFlagGates`).
  { name: '법정 서류', path: '/legal-documents', icon: ScaleIcon, flagKey: 'legal-obligation', group: 'work' },
  { name: '문서관리', path: '/reports', icon: DocumentIcon, group: 'work' }, // 보고서 + TBM 통합
  { name: '결재함', path: '/approvals', icon: InboxIcon, group: 'work' },
  { name: '현장 관리', path: '/sites', icon: BuildingIcon, group: 'manage' },
  { name: '조직 정보', path: '/organization-info', icon: InformationCircleIcon, memberOnly: true, group: 'manage' },
  // P7-g: 아래 세 항목의 축은 **권한 key**다 — 그것이 가리키는 페이지의 진입 게이트와 같은 키여야 한다
  // (`layoutNavVisibility.ts` doc의 거울 규칙: 넓으면 막다른 길, 좁으면 dead grant).
  { name: '멤버 관리', path: '/members', icon: UsersIcon, anyPermission: ['member.manage', 'member.invite'], group: 'manage' },
  {
    name: '조직 관리',
    path: '/settings/organization',
    icon: CogIcon,
    // 보일 조건은 자기 페이지 키 ∥ 자식 키(합집합)이고, **갈 곳**은 `selfPermission`이 가른다 —
    // `org.roles.manage`만 가진 사람에게 자기 `path`를 두면 클릭이 `/`로 튕긴다(막다른 길).
    anyPermission: ['org.settings.manage', 'org.roles.manage', 'announcement.manage'],
    selfPermission: 'org.settings.manage',
    group: 'manage',
    children: [
      // 결재 관리는 in-page 게이트가 없어 거울로 삼을 대상이 없다 → 현행(성질) 보존
      { name: '결재 관리', path: '/settings/approval', icon: ClipboardCheckIcon, ownerOnly: true },
      // 그룹 관리는 서버도 `isOwnerMember`이고 대응 권한 키가 없다 → 성질
      { name: '그룹 관리', path: '/settings/groups', icon: GroupIcon, ownerOnly: true, businessOnly: true },
      { name: '역할·선임', path: '/settings/roles', icon: BadgeCheckIcon, anyPermission: ['org.roles.manage'] },
      // 공지 관리는 서버가 **GET까지** `announcement.manage`로 가드하므로 내비도 같은 키다
      // (거울 규칙). 소유자는 전 키를 가지므로 이 키가 소유자를 이미 포함한다.
      { name: '공지 관리', path: '/settings/announcements', icon: MegaphoneIcon, anyPermission: ['announcement.manage'] },
    ],
  },
];

// 데스크톱 사이드바 그룹 순서·라벨 (목업 셸의 메인/업무/관리 3그룹)
const menuGroups = [
  { key: 'main', label: '메인' },
  { key: 'work', label: '업무' },
  { key: 'manage', label: '관리' },
];

// 하단 네비게이션 바 메뉴 (모바일용, 5개 이하)
const bottomNavItems = [
  { name: '홈', path: '/', icon: HomeIcon },
  { name: '스케줄', path: '/schedules', icon: CalendarIcon },
  { name: '문서', path: '/reports', icon: DocumentIcon },
  { name: '현장', path: '/sites', icon: BuildingIcon },
  { name: '더보기', path: '/more', icon: MoreIcon, isMore: true },
];

export default function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackScreenshot, setFeedbackScreenshot] = useState<Blob | null>(null);

  const handleOpenFeedback = async () => {
    setUserMenuOpen(false);
    setMoreMenuOpen(false);
    setSidebarOpen(false);
    // Screen Capture API: 화면 공유 팝업이 뜨는 동안 메뉴는 이미 닫혀 있으므로 rAF 대기 불필요
    const blob = await captureScreenshot();
    // 캡처 실패/취소 시에도 모달은 열어서 스크린샷 없이 진행
    setFeedbackScreenshot(blob);
    setFeedbackOpen(true);
  };
  const userMenuRef = useRef<HTMLDivElement>(null);
  const moreMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const deviceLayout = useDeviceLayout();
  const isMobile = deviceLayout === 'phone' || deviceLayout === 'fold';

  // 보고서 상세(/reports/:id)는 자체 하단 액션바가 있어, 폴드/모바일에서 앱 하단 네비를 숨긴다
  // (이중 하단 바 방지 → 액션바를 화면 바닥으로). Composer 작성/편집
  // (`/reports/new/composed/:templateKey`)은 두 단계 경로라 아래 정규식에 안 걸린다.
  const isReportDetailRoute = /^\/reports\/(?!new$)[^/]+$/.test(location.pathname);
  const hideBottomNav = isMobile && isReportDetailRoute;

  const bottomNavRef = useRef<HTMLElement>(null);

  // 하단 네비게이션 바 높이를 CSS 변수로 노출 (자식 컴포넌트에서 고정 바 위치 계산에 사용)
  useEffect(() => {
    const nav = bottomNavRef.current;
    // 네비가 렌더되지 않는 경우(데스크탑 또는 hideBottomNav)엔 높이 0 → 자체 액션바가 bottom:0으로 내려감
    if (!isMobile || hideBottomNav || !nav) {
      document.documentElement.style.setProperty('--bottom-nav-height', '0px');
      return;
    }

    const update = () => {
      const height = nav.offsetHeight;
      document.documentElement.style.setProperty('--bottom-nav-height', `${height}px`);
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(nav);
    return () => observer.disconnect();
  }, [isMobile, hideBottomNav]);

  // 콘텐츠 컬럼의 **좌측 오프셋**을 CSS 변수로 노출 — `--bottom-nav-height`와 같은 축(세로를 그것이,
  // 가로를 이것이 맡는다). 자식의 `position: fixed` 바는 뷰포트 기준이라 아래 `lg:pl-64`(사이드바 폭)을
  // 스스로 알지 못하고, 그래서 안쪽 `mx-auto`가 **뷰포트** 중앙에 정렬된다 — 그만큼(256/2 = 128px)
  // 왼쪽으로 쏠린다. 고정 바가 이 변수를 `left`로 읽으면 콘텐츠 컬럼 기준으로 돌아온다.
  //
  // 축이 **둘**이고 둘 다 필요하다:
  //  - `isMobile`(`useDeviceLayout`): 디바이스 판정이라 사이드바 자체가 렌더되지 않는다(폰/폴드).
  //  - `lg` 미디어쿼리(1024px): `lg:pl-64`는 미디어쿼리라 **JS가 그 상태를 직접 알지 못한다**.
  //    데스크톱이라도 창이 1024 미만이면 패딩이 안 걸리고 사이드바는 드로어(`-translate-x-full`)다.
  // ⚠️ 두 축을 각각 구독해야 한다 — `isMobile`은 훅이, 뷰포트는 아래 matchMedia가 따라간다.
  //
  // 기본값은 **설정하지 않은 쪽이 0**이다(소비처가 `var(--app-content-left, 0px)`로 읽는다) —
  // 그래서 Layout 밖(외부 결재·인쇄)과 admin은 지금 동작 그대로다.
  useEffect(() => {
    const root = document.documentElement;
    if (isMobile || typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      root.style.setProperty('--app-content-left', '0px');
      return;
    }
    // 폭은 사이드바 `w-64`(= 16rem = 256px)와 본문 `lg:pl-64`가 같은 값을 쓴다. 둘 중 하나를
    // 바꾸면 이 상수도 함께 바꿀 것(세 자리가 같은 값을 들고 있다).
    const mq = window.matchMedia('(min-width: 1024px)');
    const apply = (): void =>
      root.style.setProperty('--app-content-left', mq.matches ? '256px' : '0px');
    apply();
    mq.addEventListener('change', apply);
    return () => {
      mq.removeEventListener('change', apply);
      // Layout이 언마운트되면(로그아웃·외부 라우트 전환) 오프셋을 걷는다 — 남기면 사이드바가
      // 없는 화면의 고정 바가 그대로 들여써진다.
      root.style.setProperty('--app-content-left', '0px');
    };
  }, [isMobile]);

  const { user, logout, currentMembership, fetchUser } = useAuthStore();
  const { fetchInvitations } = useInvitationStore();
  const { openCreateInspection, openCreateSite } = useModalStore();
  const { registerHandler, isConnected } = useWebSocketStore();
  const { showToast } = useToastStore();
  const { addNotification } = useNotificationStore();
  // 빙의 여부는 Layout이 더 묻지 않는다 — 배너가 스스로 판정해 렌더하고(없으면 null) 높이 0을
  // 보고하므로, 오프셋 합산이 그 하나로 갈린다. 「뜨는가」와 「몇 px인가」를 두 곳에서 따로
  // 판정하던 것이 40px 고정 가정의 뿌리였다(2026-09-29 P6 실측).
  // 내비는 그 항목이 가리키는 페이지 게이트의 **거울**이다 — 축은 항목마다 다르다(소유자 성질 / 권한 key).
  // 근거·판정·양방향 결함(막다른 길 ↔ dead grant)은 `layoutNavVisibility.ts` doc.
  // 플래그 뒤 메뉴의 가시성 표 — 키마다 출처가 다르다. `legal-obligation`은 사용자 플래그 평가가 아니라
  // 서버 visibility다(수검업체 축이 들어 있어 `/feature-flags/me`로는 못 맞춘다). 키가 없으면 숨는다.
  const legalDocumentsVisibility = useLegalDocumentsVisibility(currentMembership?.companyId);
  const navFlagGates = { 'legal-obligation': legalDocumentsVisibility.visible };
  const navContext = resolveNavVisibilityContext(currentMembership, navFlagGates);
  // 판정은 `navContext`를 통째로 넘겨 `isNavItemVisible`·`resolveNavItemPath`가 한다(축이 항목마다
  // 다르므로 여기서 성질을 꺼내 쓰면 그 판정을 우회한다). 여기서 직접 읽는 것은 조직 형태뿐이다.
  const { isBusinessOrg } = navContext;

  // FAB Speed Dial: 대시보드/스케줄/점검/보고서 페이지에서 표시
  const fabPages = ['/', '/schedules', '/inspections', '/reports'];
  const fabPermissions = useFabPermissions();
  const fabSuppressed = useFabSuppression();
  const hasAnyFabPermission =
    fabPermissions.canCreateSite ||
    fabPermissions.canCreateInspection ||
    fabPermissions.canCreateDocument;
  const showFab =
    isMobile &&
    hasAnyFabPermission &&
    fabPages.includes(location.pathname) &&
    !fabSuppressed;

  const handleFabSelect = useCallback(
    (key: string) => {
      if (key === 'site') {
        openCreateSite();
      } else if (key === 'inspection') {
        openCreateInspection();
      } else if (key === 'document') {
        navigate('/reports/new');
      }
    },
    [openCreateSite, openCreateInspection, navigate],
  );

  const fabItems: SpeedDialItem[] = [
    {
      key: 'site',
      icon: <FactoryIcon className="w-6 h-6" />,
      label: '새 현장',
      enabled: fabPermissions.canCreateSite,
      disabledReason: fabPermissions.reasons.site,
    },
    {
      key: 'inspection',
      icon: <FabClipboardCheckIcon className="w-6 h-6" />,
      label: '새 점검',
      enabled: fabPermissions.canCreateInspection,
      disabledReason: fabPermissions.reasons.inspection,
    },
    {
      key: 'document',
      icon: <FileTextIcon className="w-6 h-6" />,
      label: '새 문서',
      enabled: fabPermissions.canCreateDocument,
      disabledReason: fabPermissions.reasons.document,
    },
  ];

  // 빙의 배너 — 동적 측정(ResizeObserver). 2026-09-29 P6 실측 전까지 `40` 고정 가정이었고
  // 실제는 44px이라 아래를 4px 덮고 있었다(아래 `totalTopPx` 주석에 경위).
  const [impersonationBannerHeight, setImpersonationBannerHeight] = useState(0);
  const handleImpersonationBannerHeight = useCallback((h: number) => {
    setImpersonationBannerHeight(h);
  }, []);

  // 기관 인증 배너 — 동적 측정(ResizeObserver). 차단성이라 상시 배너로 남는다.
  const [verificationBannerHeight, setVerificationBannerHeight] = useState(0);

  const handleVerificationBannerHeight = useCallback((h: number) => {
    setVerificationBannerHeight(h);
  }, []);

  // "확인이 필요한 항목" 바 — 인증 배너와 같은 측정 관례.
  const [actionItemsBarHeight, setActionItemsBarHeight] = useState(0);
  const handleActionItemsBarHeight = useCallback((h: number) => {
    setActionItemsBarHeight(h);
  }, []);

  // 공지 고정 띠 — 같은 측정 관례. 조치 대상이 아니라 스택의 **맨 아래**다(그 컴포넌트 JSDoc).
  const [announcementBandHeight, setAnnouncementBandHeight] = useState(0);
  const handleAnnouncementBandHeight = useCallback((h: number) => {
    setAnnouncementBandHeight(h);
  }, []);
  const companyId = currentMembership?.companyId;

  // 회사 전환을 그룹 store에 알린다 — 그룹 선택은 회사별로 저장되므로, 활성 회사가 바뀌면
  // 그 회사에 저장된 선택(없으면 null)으로 교체한다. GroupSelector는 비즈니스 조직에서만
  // 마운트돼 개인↔비즈니스 전환을 못 받으므로, 항상 마운트되는 Layout이 이 동기화를 소유한다.
  const syncGroupCompany = useGroupStore((s) => s.syncCompany);
  useEffect(() => {
    syncGroupCompany(companyId ?? null);
  }, [companyId, syncGroupCompany]);

  // 오늘 예정된 점검은 **대시보드 위젯**이 소유한다(결정 기록 2026-08-19 1항: 배너 자격은
  // 차단성 2종뿐). 여기 있던 쿼리(`['todayScheduledInspections', companyId]`)는 위젯으로 갔다.
  //
  // ⚠️ 그런데 **WS 무효화는 위젯이 가져가지 않는다** — `WebSocketProvider`의
  //    `invalidateInspectionCaches`가 그 키의 **단일 소유자**다(`ef2146ebb`). 여기 있던
  //    `INSPECTION_STATUS_CHANGED` 등록은 애초에 **죽어 있었고**(`registerHandler`가 이벤트당
  //    핸들러 하나를 교체하는데 Provider가 Layout을 감싸 나중에 등록해 이겼다) 그것을 걷어
  //    경쟁자 자체를 없앤 것이 이 커밋이다. **다시 등록하지 말 것** — 등록하면 같은 교체 규약
  //    으로 Provider의 토스트·무효화를 덮는다. 가드:
  //    `components/__tests__/inspectionInvalidationOwnership.test.ts`.

  // 로그인 시 초대 목록 조회
  useEffect(() => {
    if (user) {
      fetchInvitations();
    }
  }, [user, fetchInvitations]);

  // WebSocket 이벤트 구독: 전화번호 해제 알림
  useEffect(() => {
    if (!isConnected || !user) return;

    registerHandler<UserEventPayload>(
      ServerEvents.USER_PHONE_REMOVED,
      (data) => {
        // 현재 사용자의 이벤트인지 확인
        if (data.userId === user.id) {
          console.log('[WebSocket] Phone removed event received, refreshing user data');
          // 사용자 정보를 다시 가져와서 phoneRemovedAt 반영
          fetchUser();
        }
      }
    );
  }, [isConnected, user, registerHandler, fetchUser]);

  // WebSocket 이벤트 구독: 검토 요청 알림
  useEffect(() => {
    if (!isConnected || !user) return;

    registerHandler<ReviewRequestedPayload>(
      ServerEvents.REVIEW_REQUESTED,
      (data) => {
        console.log('[WebSocket] Review requested event received:', data);

        // 토스트 표시 (클릭 시 점검 상세 페이지의 첫 미검토 사진으로 이동)
        showToast(data.message, 'info', {
          navigateTo: `/inspections/${data.inspectionId}?scrollTo=pendingReview`,
        });

        // 알림 목록에 추가
        addNotification({
          type: 'inspection_review_requested',
          title: '검토 요청',
          message: data.message,
          data: {
            inspectionId: data.inspectionId,
            siteName: data.siteName,
            inspectionDate: data.inspectionDate,
            requestedBy: data.requestedBy,
            pendingCount: data.pendingCount,
            scrollTo: 'pendingReview', // 스크롤 파라미터
          },
        });
      }
    );
  }, [isConnected, user, registerHandler, showToast, addNotification]);

  // 경로 변경 시 관련 데이터 캐시 무효화 (페이지 이동 시 새 데이터 불러오기)
  useEffect(() => {
    const pathname = location.pathname;
    const companyId = currentMembership?.companyId;

    if (!companyId) return;

    // 경로에 따라 관련 쿼리 캐시 무효화
    // 각 페이지의 useQuery는 자동으로 refetch됨
    if (pathname === '/sites' || pathname.startsWith('/sites/')) {
      // 현장 관리 페이지
      queryClient.invalidateQueries({ queryKey: ['sites', companyId] });
      queryClient.invalidateQueries({ queryKey: ['organizationMembers', companyId] });
    } else if (pathname === '/inspections' || pathname.startsWith('/inspections/')) {
      // 점검 페이지
      queryClient.invalidateQueries({ queryKey: ['inspections', companyId] });
      queryClient.invalidateQueries({ queryKey: ['sites', companyId] });
    } else if (pathname === '/reports' || pathname.startsWith('/reports/')) {
      // 보고서 페이지 — TBM도 문서관리(/reports) 하나다(`['tbms']` queryKey를 쓰는 쿼리는 없다).
      queryClient.invalidateQueries({ queryKey: ['reports', companyId] });
      queryClient.invalidateQueries({ queryKey: ['inspections', companyId] });
    } else if (pathname === '/approvals' || pathname.startsWith('/approvals/')) {
      // 결재함 페이지
      queryClient.invalidateQueries({ queryKey: ['approvals', companyId] });
      queryClient.invalidateQueries({ queryKey: ['reports', companyId] });
    } else if (pathname === '/members' || pathname.startsWith('/members/')) {
      // 멤버 관리 페이지
      queryClient.invalidateQueries({ queryKey: ['organizationMembers', companyId] });
      queryClient.invalidateQueries({ queryKey: ['organizationInvitations', companyId] });
    } else if (pathname === '/organization-info' || pathname.startsWith('/organization-info/')) {
      // 조직 정보 페이지
      queryClient.invalidateQueries({ queryKey: ['organizationMembers', companyId] });
      queryClient.invalidateQueries({ queryKey: ['sites', companyId] });
    } else if (pathname === '/' || pathname === '/dashboard') {
      // 대시보드 - 전체 갱신
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['inspections', companyId] });
      queryClient.invalidateQueries({ queryKey: ['reports', companyId] });
      queryClient.invalidateQueries({ queryKey: ['sites', companyId] });
    }

    // 디버깅용 로그 (개발 환경에서만 출력)
    if (import.meta.env.DEV) {
      console.log('[Layout] Page navigation detected, invalidating queries for:', pathname);
    }
  }, [location.pathname, currentMembership?.companyId, queryClient]);

  // 메뉴 필터링: ownerOnly, memberOnly, businessOnly, anyPermission 조건 처리 (하위 항목에도 동일 적용).
  // 판정 본체는 `layoutNavVisibility.ts`의 순수 함수 — 여기서 축만 건넨다.
  const isMenuVisible = (item: MenuItem) => isNavItemVisible(item, navContext);

  // 부모 항목은 **보일 조건과 갈 곳이 다르다** — 자기 페이지에 못 들어가는데 자식 때문에 보이는 경우
  // 목적지를 첫 가시 자식으로 돌린다(아니면 부모 클릭이 `/`로 튕긴다). 아래 `moreMenuItems`가 이
  // 결과를 그대로 평탄화하므로 모바일 「더보기」도 같은 목적지를 쓴다.
  const filteredMenuItems = menuItems.filter(isMenuVisible).map((item) => {
    if (!item.children) return item;
    const children = item.children.filter(isMenuVisible);
    return { ...item, children, path: resolveNavItemPath(item, navContext, children) };
  });

  // 하단 네비게이션 "더보기" 메뉴에 표시할 항목들
  // 하단 바에 없는 메뉴 + 프로필/설정 — 모바일은 서브메뉴 없이 flat하게 편다
  const moreMenuItems = filteredMenuItems
    .flatMap((item) => [item, ...(item.children ?? [])])
    .filter((item) => !bottomNavItems.some((nav) => nav.path === item.path));

  // 사용자 메뉴 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target as Node)) {
        setMoreMenuOpen(false);
      }
    };

    if (userMenuOpen || moreMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenuOpen, moreMenuOpen]);

  // 경로 변경 시 더보기 메뉴 닫기
  useEffect(() => {
    setMoreMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    setUserMenuOpen(false);
    await logout();
    navigate('/login');
  };

  /**
   * 상단 스택은 **차단성이 위**다(결정 기록 2026-08-19 1항 + P0 정정).
   *
   *   빙의(측정) → 기관 인증(측정) → 확인이 필요한 항목 바(측정) → 공지 고정 띠(측정)
   *
   * 공지 띠가 **맨 아래**인 이유: 차단성도 조치 대상도 아니다(결정 2026-10-15 §B-ⓓ가 조직 공지의
   * 노출 형태로 띠를 정했고, 상한·접힘은 2026-08-19 규약을 그대로 쓴다 — `AnnouncementBand` JSDoc).
   *
   * 배너 자격은 「조치하지 않으면 계속 못 쓰는가?」로 가리고, 그 답이 참인 둘만 배너로 남았다.
   * 기관 인증은 **상태와 무관하게** 차단성이다 — `assertOrgVerifiedForExternalAction`이
   * PENDING과 REJECTED를 구분하지 않고 똑같이 막는다(P0 정정).
   *
   * ⚠️ **고정 높이 가정은 여기 하나도 없다** — 셋 다 `onHeightChange`(ResizeObserver) 실측이다.
   * 빙의 배너는 2026-09-29 P6 dev 실측까지 `40`으로 가정돼 있었는데 **실제 높이는 44px**이라
   * (뷰포트 1278px 기준) 그 아래 배너·바·본문을 **4px 덮고 있었다.** 2026-08-19에 고친
   * `PhoneRemovedBanner`의 「56 가정 vs 실제 62」와 **같은 종류의 결함**이고, 이것이 그 마지막
   * 자리였다. 높이는 문구 줄바꿈·폰트·뷰포트로 바뀌므로 **상수로 적으면 반드시 어긋난다** —
   * 새 배너를 더할 때도 이 관례를 따를 것(가드: `__tests__/bannerStackMeasuredHeight.test.ts`).
   */
  const totalTopPx =
    impersonationBannerHeight +
    verificationBannerHeight +
    actionItemsBarHeight +
    announcementBandHeight;

  return (
    <div
      className="min-h-screen bg-surface"
      style={{
        paddingTop: totalTopPx,
        // 데스크톱 화면 전용 바탕(목업 body 배경) — print CSS는 별도 경로라 영향 없음
        ...(isMobile
          ? {}
          : {
              backgroundImage:
                'radial-gradient(900px 500px at 92% -10%, #e0e7ff 0%, transparent 55%), radial-gradient(800px 460px at -4% 102%, #fde9d6 0%, transparent 50%)',
            }),
      }}
    >
      {/* 빙의 모드 배너 — 언제나 최상단(top-0)이라 topOffset이 없다. 높이만 보고한다. */}
      <ImpersonationBanner onHeightChange={handleImpersonationBannerHeight} />

      {/* 기관 인증 심사 대기/거부 배너 — 차단성이라 상시 배너(어떤 상태든) */}
      <VerificationPendingBanner
        topOffset={impersonationBannerHeight}
        onHeightChange={handleVerificationBannerHeight}
      />

      {/*
        확인이 필요한 항목 바 — 차단성 배너 **아래**. 번호 회수·내 중복 인원이 배너에서 여기로
        내려왔다(결정 기록 1항). 항목이 0건이면 스스로 렌더하지 않고 높이 0을 보고한다.
      */}
      <ActionItemsBar
        topOffset={impersonationBannerHeight + verificationBannerHeight}
        onHeightChange={handleActionItemsBarHeight}
      />

      {/*
        공지 고정 띠 — 스택의 맨 아래. 후보가 0건이면 스스로 렌더하지 않고 높이 0을 보고한다.
        상한 2·접힘은 이 컴포넌트가 2026-08-19 규약으로 적용하고 서버는 후보만 돌려준다.
      */}
      <AnnouncementBand
        topOffset={impersonationBannerHeight + verificationBannerHeight + actionItemsBarHeight}
        onHeightChange={handleAnnouncementBandHeight}
      />

      {/*
        일반 팝업 호스트 — **배너 스택과 다른 축이다.** 배너는 「조치하지 않으면 계속 못 쓰는」
        상태라 상한이 2로 고정돼 있고(결정 2026-08-19 배너 슬롯 정책), 팝업은 「확인하면 닫히는」
        방송이다. 그래서 `topOffset`을 받지도 높이를 보고하지도 않는다 — 포탈 모달이라 흐름에
        없고 배너 오프셋 누적에 들어가지 않는다.

        ⚠️ 결정 2026-10-15 §B-ⓓ가 **「전사는 팝업, 조직은 고정 띠」**로 정했으므로 팝업이 둘
        겹치는 일은 구조적으로 없어진다. 그래도 `PopupHost`가 **큐로 한 장씩** 띄우는 로직을
        갖고 있는 이유: 그 가름은 *소비자*(P3·P4)의 선택이고 이 기제는 그것을 강제하지 않는다 —
        소비자가 둘 다 팝업으로 요청하면 쌓일 수 있고, 그때 두 장이 겹쳐 뜨는 것이 더 나쁘다.
      */}
      <PopupHost />

      {/*
        내 중복 인원 판정 모달 — 바·벨 **두 표면**이 같은 모달을 열므로 여기 한 번만 마운트하고
        열림 상태는 항목 store가 소유한다.
      */}
      <MyDuplicateModal />

      {/* 모바일 사이드바 오버레이 (데스크톱 뷰에서 lg 미만일 때만) */}
      {!isMobile && sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* 사이드바 (모바일에서는 하단 네비게이션 사용) */}
      {!isMobile && (
        <aside
        className={`fixed bottom-0 left-0 z-50 w-64 bg-white border-r border-line shadow-lg lg:shadow-none transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        style={{ top: `${totalTopPx}px`, height: `calc(100dvh - ${totalTopPx}px)` }}
      >
        <div className="flex flex-col h-full overflow-hidden">
          {/* 로고 */}
          <div className="flex items-center justify-between h-16 px-4">
            <Link to="/" className="flex items-center gap-2.5">
              <span
                className="relative grid h-[30px] w-[30px] flex-none place-items-center rounded-[9px] text-white"
                style={{
                  background:
                    'conic-gradient(from 220deg at 50% 50%, #4f46e5, #6366f1, #8b5cf6, #4f46e5)',
                  boxShadow: '0 6px 14px -6px rgba(79,70,229,.55)',
                }}
              >
                <span aria-hidden="true" className="absolute inset-[6px] rounded-[5px] bg-white" />
                <ShieldIcon className="relative z-[1] h-3.5 w-3.5 text-brand-600" />
              </span>
              <span className="text-xl font-extrabold tracking-tight text-ink-900">
                Safety<span className="text-brand-600">Hub</span>
              </span>
            </Link>
            <div className="flex items-center gap-1">
              {/* 알림 (lg 이상에서만 표시, 모바일은 상단 헤더에서 표시) */}
              <div className="hidden lg:block">
                <NotificationDropdown />
              </div>
              <button
                className="lg:hidden p-2 rounded-card-sm text-ink-500 hover:bg-surface-hover"
                onClick={() => setSidebarOpen(false)}
              >
                <XIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* 조직/그룹 선택 — 시안 `.ws`는 **박스 자체가 클릭 대상**이라 장식(테두리·soft 면·radius)은
              셀렉터 버튼이 소유한다. 여기 래퍼가 같은 장식을 또 두면 박스가 이중으로 겹쳐 보인다
              (2026-07-28 실측). 래퍼는 바깥 여백과 두 셀렉터 간격만 담당. */}
          <div className="mx-3 mb-3 space-y-1.5">
            <OrganizationSelector />
            {/* 그룹 선택 (비즈니스 조직 + 그룹 2개 이상일 때만 표시) */}
            {isBusinessOrg && (
              <GroupSelector groups={currentMembership?.company.groups ?? []} />
            )}
          </div>

          {/* 네비게이션 메뉴 — 시각 그룹 3개(메인/업무/관리), 권한 필터는 filteredMenuItems 그대로 */}
          <nav className="flex-1 px-2 pb-4 overflow-y-auto min-h-0">
            {menuGroups.map((group) => {
              const groupItems = filteredMenuItems.filter((item) => item.group === group.key);
              if (groupItems.length === 0) return null;
              return (
                <div key={group.key} className="pt-1.5 pb-1">
                  <div className="px-3 pt-2 pb-1 text-[11px] font-bold uppercase tracking-[0.08em] text-ink-400">
                    {group.label}
                  </div>
                  <div className="space-y-0.5">
                    {groupItems.map((item) => {
                      const isActive = location.pathname === item.path ||
                        (item.path === '/reports' && location.pathname.startsWith('/reports')) ||
                        (item.path === '/legal-documents' && location.pathname.startsWith('/legal-documents/'));
                      const subItems = item.children ?? [];
                      const isSubActive = subItems.some((sub) => location.pathname === sub.path);
                      // 펼침은 라우트에서 파생 — 부모나 하위가 활성일 때만 하위를 보인다
                      const showSubItems = subItems.length > 0 && (isActive || isSubActive);
                      const displayName = item.name;
                      return (
                        <div key={item.path} className="space-y-0.5">
                          <Link
                            to={item.path}
                            className={`relative flex items-center px-3 py-2 text-sm rounded-[10px] transition-colors ${
                              isActive
                                ? 'bg-gradient-to-br from-brand-50 to-white font-bold text-brand-700'
                                : isSubActive
                                  ? 'font-medium text-ink-900 hover:bg-surface-hover'
                                  : 'font-medium text-ink-600 hover:bg-surface-hover hover:text-ink-900'
                            }`}
                            onClick={() => setSidebarOpen(false)}
                          >
                            {isActive && (
                              <span
                                aria-hidden="true"
                                className="absolute -left-2 top-[7px] bottom-[7px] w-[3px] rounded-r-[3px] bg-brand-600"
                              />
                            )}
                            <item.icon
                              className={`mr-3 h-5 w-5 flex-none ${
                                isActive || isSubActive ? 'text-brand-600' : 'text-ink-400'
                              }`}
                            />
                            {displayName}
                          </Link>
                          {showSubItems &&
                            subItems.map((sub) => {
                              const isSubItemActive = location.pathname === sub.path;
                              return (
                                <Link
                                  key={sub.path}
                                  to={sub.path}
                                  className={`relative flex items-center py-1.5 pl-8 pr-3 text-sm rounded-[10px] transition-colors ${
                                    isSubItemActive
                                      ? 'bg-gradient-to-br from-brand-50 to-white font-bold text-brand-700'
                                      : 'font-medium text-ink-500 hover:bg-surface-hover hover:text-ink-900'
                                  }`}
                                  onClick={() => setSidebarOpen(false)}
                                >
                                  {isSubItemActive && (
                                    <span
                                      aria-hidden="true"
                                      className="absolute -left-2 top-[5px] bottom-[5px] w-[3px] rounded-r-[3px] bg-brand-600"
                                    />
                                  )}
                                  <sub.icon
                                    className={`mr-2.5 h-4 w-4 flex-none ${
                                      isSubItemActive ? 'text-brand-600' : 'text-ink-400'
                                    }`}
                                  />
                                  {sub.name}
                                </Link>
                              );
                            })}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </nav>

          {/* 하단 side-foot 톤: 사용량 + 프로필 (구분선은 UsageIndicator 자체 border-t) */}
          <div className="bg-surface-soft">
          {/* 사용량 표시 */}
          <UsageIndicator />

          {/* 사용자 정보 (드롭다운 메뉴) */}
          <div className="p-4 relative" ref={userMenuRef}>
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="w-full flex items-center p-2 -m-2 rounded-control hover:bg-white transition-colors"
            >
              <div className="flex-shrink-0">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-brand-500 to-brand-600 shadow-[0_4px_10px_-4px_rgba(79,70,229,.5)] flex items-center justify-center">
                  <span className="text-white font-bold">
                    {user?.name?.charAt(0) || 'U'}
                  </span>
                </div>
              </div>
              <div className="ml-3 flex-1 min-w-0 text-left">
                <p className="text-sm font-medium text-ink-900 truncate">{user?.name}</p>
                <p className="text-xs text-ink-500 truncate">{user ? getUserIdentifierFromUser(user) : '-'}</p>
              </div>
              <ChevronUpIcon className={`h-5 w-5 text-ink-400 transition-transform ${userMenuOpen ? '' : 'rotate-180'}`} />
            </button>

            {/* 드롭다운 메뉴 */}
            {userMenuOpen && (
              <div className="absolute bottom-full left-4 right-4 mb-2 bg-white rounded-card shadow-card border border-line py-1 z-50">
                <Link
                  to="/profile"
                  onClick={() => {
                    setUserMenuOpen(false);
                    setSidebarOpen(false);
                  }}
                  className="flex items-center px-4 py-2 text-sm font-medium text-ink-700 hover:bg-surface-hover"
                >
                  <UserIcon className="h-5 w-5 mr-3 text-ink-400" />
                  내 프로필
                </Link>
                <Link
                  to="/settings/account"
                  onClick={() => {
                    setUserMenuOpen(false);
                    setSidebarOpen(false);
                  }}
                  className="flex items-center px-4 py-2 text-sm font-medium text-ink-700 hover:bg-surface-hover"
                >
                  <SettingsIcon className="h-5 w-5 mr-3 text-ink-400" />
                  설정
                </Link>
                <Link
                  to="/settings/billing"
                  onClick={() => {
                    setUserMenuOpen(false);
                    setSidebarOpen(false);
                  }}
                  className="flex items-center px-4 py-2 text-sm font-medium text-ink-700 hover:bg-surface-hover"
                >
                  <CreditCardIcon className="h-5 w-5 mr-3 text-ink-400" />
                  구독 관리
                </Link>
                {/* 공지 게시판 — 「업데이트 내역」의 형제다(둘 다 **보존** 축이고 권한을 요구하지
                    않는다). 사이드바가 아니라 이 메뉴에 두는 이유가 그 선례다. */}
                <Link
                  to="/announcements"
                  onClick={() => {
                    setUserMenuOpen(false);
                    setSidebarOpen(false);
                  }}
                  className="flex items-center px-4 py-2 text-sm font-medium text-ink-700 hover:bg-surface-hover"
                >
                  <MegaphoneIcon className="h-5 w-5 mr-3 text-ink-400" />
                  공지
                </Link>
                <Link
                  to="/release-notes"
                  onClick={() => {
                    setUserMenuOpen(false);
                    setSidebarOpen(false);
                  }}
                  className="flex items-center px-4 py-2 text-sm font-medium text-ink-700 hover:bg-surface-hover"
                >
                  <ReleaseNotesIcon className="h-5 w-5 mr-3 text-ink-400" />
                  업데이트 내역
                </Link>
                <button
                  onClick={handleOpenFeedback}
                  className="w-full flex items-center px-4 py-2 text-sm font-medium text-ink-700 hover:bg-surface-hover"
                >
                  <FeedbackIcon className="h-5 w-5 mr-3 text-ink-400" />
                  피드백 보내기
                </button>
                {/* 세션 이탈 액션은 탐색 항목들과 구분선으로 가른다 — 공용 드롭다운(위험 액션 분리) 결 */}
                <div className="my-1 border-t border-line-soft" />
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center px-4 py-2 text-sm font-medium text-ink-700 hover:bg-surface-hover"
                >
                  <LogoutIcon className="h-5 w-5 mr-3 text-ink-400" />
                  로그아웃
                </button>
              </div>
            )}
          </div>
          </div>
        </div>
      </aside>
      )}

      {/* 메인 컨텐츠 영역 */}
      {/* ⚠️ 이 `lg:pl-64`(256px)가 콘텐츠 컬럼의 좌측 오프셋이다 — 바꾸면 위
          `--app-content-left` 설정부(그리고 사이드바 `w-64`)도 같은 값으로 함께 바꿀 것.
          흐름 안의 자식은 이 패딩을 자동으로 따르지만 `position: fixed` 자식은 못 따른다. */}
      <div className={`flex flex-col min-h-screen ${isMobile ? '' : 'lg:pl-64'}`}>
        {/* 상단 헤더 (모바일) - 스크롤 시에도 고정 */}
        {isMobile ? (
          <header
            className="sticky z-[60] flex items-center justify-between h-14 px-4 bg-white shadow-sm"
            style={{ top: `${totalTopPx}px` }}
          >
            <Link to="/" className="text-lg font-bold text-brand-600">SafetyHub</Link>
            <div className="flex items-center gap-1">
              {/* 조직 선택 드롭다운 */}
              <OrganizationSelector compact />
              <NotificationDropdown />
            </div>
          </header>
        ) : (
          <header
            className="lg:hidden sticky z-[60] flex items-center justify-between h-16 px-4 bg-white shadow-sm"
            style={{ top: `${totalTopPx}px` }}
          >
            <button
              className="p-2 rounded-card-sm text-ink-500 hover:bg-surface-hover"
              onClick={() => setSidebarOpen(true)}
            >
              <MenuIcon className="h-6 w-6" />
            </button>
            <Link to="/" className="text-lg font-bold text-brand-600">SafetyHub</Link>
            <div className="flex items-center gap-1">
              <NotificationDropdown />
            </div>
          </header>
        )}

        {/* 페이지 컨텐츠 */}
        <main className={`flex-1 p-4 lg:p-6 ${isMobile && !hideBottomNav ? 'pb-20' : ''}`}>{children}</main>
      </div>

      {/* 하단 네비게이션 바 (모바일) — 작성/상세 등 자체 액션바가 있는 전체화면에서는 숨김 */}
      {!hideBottomNav && isMobile && (
        <nav ref={bottomNavRef} className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-line safe-area-bottom">
          <div className="flex items-center justify-around h-16">
            {bottomNavItems.map((item) => {
              const isActive = item.isMore
                ? moreMenuOpen
                : item.path === '/'
                  ? location.pathname === '/'
                  : location.pathname.startsWith(item.path);

              if (item.isMore) {
                return (
                  <div key={item.path} className="relative" ref={moreMenuRef}>
                    <button
                      onClick={() => setMoreMenuOpen(!moreMenuOpen)}
                      className={`flex flex-col items-center justify-center w-16 h-full ${
                        isActive ? 'text-brand-600' : 'text-ink-500'
                      }`}
                    >
                      <item.icon className="h-6 w-6" />
                      <span className="text-xs mt-1">{item.name}</span>
                    </button>

                    {/* 더보기 팝업 메뉴 */}
                    {moreMenuOpen && (
                      <div className="absolute bottom-full right-0 mb-2 w-56 bg-white rounded-card shadow-card border border-line py-2 max-h-80 overflow-y-auto">
                        {/* 추가 메뉴 항목들 */}
                        {moreMenuItems.map((menuItem) => {
                          const menuActive = location.pathname === menuItem.path;
                          const displayName = menuItem.name;
                          return (
                            <Link
                              key={menuItem.path}
                              to={menuItem.path}
                              className={`flex items-center px-4 py-3 text-sm ${
                                menuActive ? 'bg-brand-50 text-brand-600' : 'text-ink-700 hover:bg-surface-hover'
                              }`}
                            >
                              <menuItem.icon className={`h-5 w-5 mr-3 ${menuActive ? 'text-brand-600' : 'text-ink-400'}`} />
                              {displayName}
                            </Link>
                          );
                        })}

                        {/* 구분선 */}
                        <div className="my-2 border-t border-line-soft" />

                        {/* 프로필/설정 메뉴 */}
                        <Link
                          to="/profile"
                          className="flex items-center px-4 py-3 text-sm text-ink-700 hover:bg-surface-hover"
                        >
                          <UserIcon className="h-5 w-5 mr-3 text-ink-400" />
                          내 프로필
                        </Link>
                        <Link
                          to="/settings/account"
                          className="flex items-center px-4 py-3 text-sm text-ink-700 hover:bg-surface-hover"
                        >
                          <SettingsIcon className="h-5 w-5 mr-3 text-ink-400" />
                          설정
                        </Link>
                        <Link
                          to="/settings/billing"
                          className="flex items-center px-4 py-3 text-sm text-ink-700 hover:bg-surface-hover"
                        >
                          <CreditCardIcon className="h-5 w-5 mr-3 text-ink-400" />
                          구독 관리
                        </Link>
                        {/* 데스크톱 사용자 메뉴에만 있던 항목 — 모바일(phone/fold)은 사이드바가
                            렌더되지 않아 2026-10-19까지 진입점이 0개였다(dev 실측). 자리는 데스크톱과
                            같게 「구독 관리」 아래로 둔다 — 그 둘과 「피드백 보내기」가 계정·제품 계열이다. */}
                        <Link
                          to="/announcements"
                          className="flex items-center px-4 py-3 text-sm text-ink-700 hover:bg-surface-hover"
                        >
                          <MegaphoneIcon className="h-5 w-5 mr-3 text-ink-400" />
                          공지
                        </Link>
                        <Link
                          to="/release-notes"
                          className="flex items-center px-4 py-3 text-sm text-ink-700 hover:bg-surface-hover"
                        >
                          <ReleaseNotesIcon className="h-5 w-5 mr-3 text-ink-400" />
                          업데이트 내역
                        </Link>
                        <button
                          onClick={handleOpenFeedback}
                          className="w-full flex items-center px-4 py-3 text-sm text-ink-700 hover:bg-surface-hover"
                        >
                          <FeedbackIcon className="h-5 w-5 mr-3 text-ink-400" />
                          피드백 보내기
                        </button>
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center px-4 py-3 text-sm text-ink-700 hover:bg-surface-hover"
                        >
                          <LogoutIcon className="h-5 w-5 mr-3 text-ink-400" />
                          로그아웃
                        </button>
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex flex-col items-center justify-center w-16 h-full ${
                    isActive ? 'text-brand-600' : 'text-ink-500'
                  }`}
                >
                  <item.icon className="h-6 w-6" />
                  <span className="text-xs mt-1">{item.name}</span>
                </Link>
              );
            })}
          </div>
        </nav>
      )}

      {/* FAB Speed Dial: 새 현장/점검/문서 (모바일/폴드, 화이트리스트 페이지, 권한 있는 사용자) */}
      {showFab && <SpeedDialFab items={fabItems} onSelect={handleFabSelect} />}

      {/* 초대 모달 */}
      <InvitationModal />

      {/* 피드백 모달 */}
      <FeedbackModal
        isOpen={feedbackOpen}
        onClose={() => { setFeedbackOpen(false); setFeedbackScreenshot(null); }}
        initialScreenshot={feedbackScreenshot}
      />
    </div>
  );
}

// 아이콘 컴포넌트들
function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function HomeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
      />
    </svg>
  );
}

function DocumentIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );
}

function BuildingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
      />
    </svg>
  );
}

function CogIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  );
}

function InboxIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
      />
    </svg>
  );
}

function MenuIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 6h16M4 12h16M4 18h16"
      />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}

function LogoutIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
      />
    </svg>
  );
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
      />
    </svg>
  );
}

function UserIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
      />
    </svg>
  );
}

function ChevronUpIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
    </svg>
  );
}

function ClipboardCheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
      />
    </svg>
  );
}

/** 저울 — 법정 서류(법령이 요구하는 의무) */
function ScaleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3"
      />
    </svg>
  );
}

function CreditCardIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
      />
    </svg>
  );
}

function SettingsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function InformationCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function ReleaseNotesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );
}

// 공지(확성기) — 「업데이트 내역」(문서)과 시각적으로 갈라야 두 항목이 나란히 구분된다.
function MegaphoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z"
      />
    </svg>
  );
}

function MoreIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 6h16M4 12h16M4 18h16"
      />
    </svg>
  );
}

function FeedbackIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
      />
    </svg>
  );
}

/** 역할·선임 메뉴 — heroicons badge-check(outline). */
function BadgeCheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
      />
    </svg>
  );
}

function GroupIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
      />
    </svg>
  );
}

